/* eslint-disable array-callback-return */
import { alpha } from "@mui/material/styles";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  IconButton,
  Tooltip,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { visuallyHidden } from "@mui/utils";
import { IconFileExport, IconSearch, IconTrash } from "@tabler/icons";
import CustomCheckbox from "../forms/theme-elements/CustomCheckbox";
import { useEffect, useRef, useState } from "react";
import RowTable from "./RowTable";
import { useNavigate } from "react-router";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useMutation } from "@tanstack/react-query";
import { exportTable } from "src/queries/query";
import { toast } from "react-toastify";

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    headCells,
    withDeleteItem,
  } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(
      property,
      orderBy === property && order === "asc" ? "desc" : "asc",
    ); // Use property correctly
  };

  return (
    <TableHead>
      <TableRow>
        {withDeleteItem ? (
          <TableCell padding="checkbox">
            <CustomCheckbox
              color="primary"
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputprops={{
                "aria-label": "select all desserts",
              }}
            />
          </TableCell>
        ) : (
          <TableCell padding="checkbox"></TableCell>
        )}
        {headCells.map((headCell) => {
          if (headCell.hide) return null;
          return (
            <TableCell
              key={headCell.id} // Use id here
              align={headCell.numeric ? "right" : "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.id ? order : false} // Use id here
              style={{
                whiteSpace: "nowrap",
              }}
            >
              {headCell.withSort ? (
                <TableSortLabel
                  active={orderBy === headCell.id} // Use id here
                  direction={orderBy === headCell.id ? order : "asc"} // Use id here
                  onClick={createSortHandler(headCell.id)} // Use id here to pass the column field
                >
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </TableSortLabel>
              ) : (
                headCell.label
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

const EnhancedTableToolbar = (props) => {
  const {
    numSelected,
    handleSearch,
    search,
    onPressDelete,
    selected,
    setSelected,
    withSearch,
    withDeleteItem,
    canBack,
    nameTable,
    modelName,
  } = props;
  const navigate = useNavigate();

  const { mutate: exportData, isPending: isExporting } = useMutation({
    mutationFn: (values) => exportTable(values),
    onSuccess: () => {
      console.log("Export started successfully!");
      toast.success("Export started successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message ?? `Failed to export data`);
    },
  });

  // Handle export button click
  const handleExport = () => {
    if (modelName) {
      exportData({ model: modelName });
    } else {
      toast.error("Model name is required for export");
    }
  };

  return (
    <>
      {numSelected === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {canBack ? (
              <IconButton
                onClick={() => {
                  navigate(-1);
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            ) : null}
            <Typography variant="h6" component="div">
              {nameTable}
            </Typography>
          </Box>

          <Tooltip title="Export Data">
            <IconButton
              onClick={handleExport}
              disabled={isExporting}
              color="primary"
            >
              {isExporting ? (
                <CircularProgress size={24} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "18px",
                    gap: 1,
                  }}
                >
                  Export
                  <IconFileExport />
                </Box>
              )}
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {
        numSelected === 0 &&
          (canBack ? (
            <IconButton
              style={{ justifyContent: "start", width: "50%" }}
              onClick={() => {
                navigate(-1);
              }}
            >
              <ArrowBackIcon />
              <b style={{ fontSize: 20, color: "black", marginLeft: 10 }}>
                {nameTable}
              </b>
            </IconButton>
          ) : (
            <b style={{ fontSize: 20, width: "50%" }}>{nameTable}</b>
          ))

        // <div style={{ width: "50%" }}>
        //   <b style={{ fontSize: 20 }}>{nameTable}</b>
        // </div>
      }
      {numSelected > 0 && withDeleteItem && (
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            ...(numSelected > 0 &&
              withDeleteItem && {
                bgcolor: (theme) =>
                  alpha(
                    theme.palette.primary.main,
                    theme.palette.action.activatedOpacity,
                  ),
              }),
          }}
        >
          {numSelected > 0 && withDeleteItem ? (
            <Typography
              sx={{ flex: "1 1 100%" }}
              color="inherit"
              variant="subtitle2"
              component="div"
            >
              {numSelected} selected
            </Typography>
          ) : (
            <Box sx={{ flex: "1 1 100%" }}>
              {withSearch && (
                <TextField
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconSearch size="1.1rem" />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Search"
                  size="small"
                  onChange={handleSearch}
                  value={search}
                />
              )}
            </Box>
          )}

          {numSelected > 0 && withDeleteItem ? (
            <Tooltip title="Delete">
              <IconButton>
                <IconTrash
                  onClick={() => {
                    onPressDelete(selected);
                    setSelected([]);
                  }}
                  width="18"
                />
              </IconButton>
            </Tooltip>
          ) : null}
        </Toolbar>
      )}
    </>
  );
};

const DynamicTable = ({
  handleSearch,
  search,
  selected,
  setSelected,
  setPage,
  page,
  setRowsPerPage,
  rowsPerPage,
  items,
  headCells,
  onPressDelete,
  Listdropdown,
  onPressItem,
  onRequestSort,
  columns,
  withOption,
  loadingData,
  rowsPerPageOptions,
  totalItem,
  onPressAnyAction,
  onPressAdd,
  loadingEditdatatype,
  withPagination = true,
  withSearch = true,
  withAddButton = true,
  withDeleteItem = true,
  withEditItem = true,
  withRowPage = true,
  canBack = false,
  nameTable,
  isLogs = false,
  setItems = () => {},
  onDragEnd = () => {},
  isDroppableEnabled = false,
  isLoadingUpdateActive = false,
  withViewItem = false,
  onPressViewItem,
  searchParams,
  modelName,
}) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  const reorderList = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const handleDragEnd = (result) => {
    if (!result.destination) {
      // Drop outside the droppable area
      return;
    }
    let indexitem = items.findIndex((res) => res._id === result.draggableId); // this item
    let newOrderNumber = items[result.destination.index]?.order_number;
    let oldOrderNumber = items[indexitem]?.order_number;

    const reorderedItems = reorderList(
      items,
      result.source.index,
      result.destination.index,
    );
    if (setItems) {
      setItems(reorderedItems); // Update the reordered items
    }
    if (onDragEnd) {
      onDragEnd({
        newOrderNumber,
        oldOrderNumber,
        item_id: items[indexitem]?._id,
      });
    }
  };

  // This is for the sorting
  const handleRequestSort = (property, order) => {
    setSelected([]);
    setOrder(order); // 'asc' or 'desc'
    setOrderBy(property); // This is your column field (e.g., 'name', 'price', etc.)
    onRequestSort(property, order); // Pass the correct property (tableName) and sorting order
  };
  // This is for select all the row
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = items.map((n) => n._id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  // This is for the single row sleect
  const handleClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setSelected([]);
    setPage(newPage + 1); // Set page with 1-based indexing for UI or API
  };

  const handleChangeRowsPerPage = (event) => {
    setSelected([]);
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - items.length) : 0;
  const ref = useRef(null);

  const numberData = new Array(rowsPerPage).fill(null);
  const [offsetWidth, setOffsetWidth] = useState(0);

  useEffect(() => {
    // Check if the ref is available
    if (ref.current) {
      // Access the offsetWidth and update the state
      setOffsetWidth(ref.current.offsetWidth);
    }
  }, [ref]);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box
        sx={{
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "100%",
          }}
        >
          <EnhancedTableToolbar
            numSelected={selected.length}
            withSearch={withSearch}
            search={search}
            handleSearch={(event) => {
              setSelected([]);
              handleSearch(event);
            }}
            setSelected={setSelected}
            onPressDelete={onPressDelete}
            selected={selected}
            onPressAdd={onPressAdd}
            withAddButton={withAddButton}
            withDeleteItem={withDeleteItem}
            canBack={canBack}
            nameTable={nameTable}
            modelName={modelName}
          />
          <Paper
            variant="outlined"
            sx={{
              width: "100%",
            }}
          >
            <TableContainer
              ref={ref}
              sx={{
                width: "100%",
              }}
            >
              <Droppable droppableId="table-droppable">
                {(provided) => (
                  <Table
                    // sx={{ minWidth: 750 }}
                    aria-labelledby="tableTitle"
                    size={"medium"}
                    ref={provided.innerRef} // Proper ref setting
                    {...provided.droppableProps} // Pass droppableProps
                    sx={{
                      width: "100%",
                    }}
                  >
                    <EnhancedTableHead
                      numSelected={selected.length}
                      order={order}
                      orderBy={orderBy}
                      onSelectAllClick={handleSelectAllClick}
                      onRequestSort={handleRequestSort}
                      rowCount={items.length}
                      withDeleteItem={withDeleteItem}
                      headCells={headCells}
                    />
                    <TableBody
                      sx={{
                        width: "100%",
                      }}
                    >
                      {!loadingData &&
                        items.map((row, index) => {
                          return (
                            <Draggable
                              key={row._id}
                              draggableId={row._id?.toString()}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <RowTable
                                  provided={
                                    !isDroppableEnabled ? null : provided
                                  }
                                  headCells={headCells}
                                  row={row}
                                  index={index}
                                  searchParams={searchParams}
                                  columns={columns}
                                  handleClick={handleClick}
                                  onPressItem={onPressItem}
                                  isSelected={isSelected}
                                  Listdropdown={Listdropdown}
                                  isLogs={isLogs}
                                  withOption={withOption}
                                  onPressAnyAction={onPressAnyAction}
                                  loadingEditdatatype={loadingEditdatatype}
                                  withDeleteItem={withDeleteItem}
                                  withEditItem={withEditItem}
                                  snapshot={snapshot}
                                  withViewItem={withViewItem}
                                  onPressViewItem={onPressViewItem}
                                />
                              )}
                            </Draggable>
                          );
                        })}

                      {loadingData ? (
                        <>
                          <div
                            style={{
                              width: offsetWidth,
                              position: "absolute",
                            }}
                          >
                            {numberData.map((res) => {
                              return (
                                <>
                                  <Skeleton height={40} animation="wave" />
                                  <div style={{ height: "1vh" }} />
                                </>
                              );
                            })}
                          </div>
                          <div
                            style={{ height: `${numberData.length * 5.5}vh` }}
                          />
                        </>
                      ) : items.length === 0 ? (
                        <TableRow
                          style={{
                            height: 53 * emptyRows,
                          }}
                        >
                          <TableCell colSpan={6}>
                            <div>No data found</div>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                )}
              </Droppable>
            </TableContainer>
            {withPagination && (
              <TablePagination
                rowsPerPageOptions={[]} // Removes the dropdown by passing an empty array
                component="div"
                count={totalItem}
                rowsPerPage={rowsPerPage}
                page={page - 1} // Adjust for zero-based pagination if necessary
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="" // Hides the "Rows per page" label
              />
            )}
          </Paper>
        </Box>
      </Box>
    </DragDropContext>
  );
};

export default DynamicTable;
